import { Button, TextField } from "@material-ui/core";

import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Fade,
  IconButton,
  InputAdornment,
  Link,
  Typography,
} from "@mui/material";
import axios from "axios";
import { Eye, EyeClosed } from "phosphor-react";
import { useState } from "react";
import { createCookie } from "../../Components/authorization";
import { MovieColabLogo } from "../../Components/Utilities/MovieColabLogo";
import signupAndLoginStyles from "./signupAndLoginStyle";

const SignupPage = () => {
  const classes = signupAndLoginStyles();
  const [showPassword, setShowPassword] = useState(false);

  const [formData, setFormData] = useState({
    email: "",
    password: "",
    first_name: "",
    last_name: "",
  });

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.first_name) newErrors.first_name = "First name is required";
    if (!formData.last_name) newErrors.last_name = "Last name is required";

    if (!formData.email) newErrors.email = "Email is required";
    else if (!/\S+@\S+\.\S+/.test(formData.email))
      newErrors.email = "Email is invalid";

    if (!formData.password) newErrors.password = "Password is required";
    else if (formData.password.length < 6)
      newErrors.password = "Password must be at least 6 characters";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field) => (event) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });

    if (errors[field]) {
      setErrors({ ...errors, [field]: "" });
    }

    if (loginError) setLoginError("");
    if (successMessage) setSuccessMessage("");
  };

  const handleSignup = async (event) => {
    event.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    setLoginError("");
    setSuccessMessage("");
    setErrors({});

    try {
      const payload = {
        email: formData.email,
        password: formData.password,
        first_name: formData.first_name,
        last_name: formData.last_name,
      };

      const res = await axios.post(
        `${process.env.REACT_APP_BASE_URL}signup/`,
        payload,
      );

      setSuccessMessage("Signup successful. Please wait logging you in");

      if (res) {
        setTimeout(async () => {
          const loginRes = await axios.post(
            `${process.env.REACT_APP_BASE_URL}login/`,
            {
              email: formData.email,
              password: formData.password,
            },
          );
          if (loginRes) {
            createCookie("refresh", res.data.refresh);
            createCookie("access", res.data.access);
            window.location.href = "/";
          }
        }, 300);
      }

      setFormData({ email: "", password: "", first_name: "", last_name: "" });
      setErrors({});
      console.log("Signup success:", res.data);
    } catch (error) {
      console.log(error);
      const respData = error?.response?.data;

      // try to extract field errors and general messages
      const fieldErrors = {};
      const otherMessages = [];

      if (respData) {
        if (typeof respData === "string") {
          otherMessages.push(respData);
        } else if (Array.isArray(respData)) {
          otherMessages.push(respData.join(" "));
        } else {
          for (const [key, value] of Object.entries(respData)) {
            if (Array.isArray(value)) {
              const message = value.join(" ");
              // map typical form fields to fieldErrors
              if (
                ["email", "password", "first_name", "last_name"].includes(key)
              ) {
                fieldErrors[key] = message;
              } else if (
                key === "non_field_errors" ||
                key === "detail" ||
                key === "message"
              ) {
                otherMessages.push(message);
              } else {
                otherMessages.push(`${key}: ${message}`);
              }
            } else if (typeof value === "object" && value !== null) {
              otherMessages.push(`${key}: ${JSON.stringify(value)}`);
            } else if (value) {
              otherMessages.push(String(value));
            }
          }
        }
      } else {
        otherMessages.push(
          error?.message || "Signup failed. Please try again.",
        );
      }

      if (Object.keys(fieldErrors).length) {
        setErrors((prev) => ({ ...prev, ...fieldErrors }));
      }

      const apiMessage = otherMessages.length
        ? otherMessages.join(" | ")
        : "Signup failed. Please try again.";
      setLoginError(apiMessage);
      console.error("Signup error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Box className={classes.loginContainer}>
      {/* Purple Glow */}
      <div className={classes.glow} />

      <Box sx={{ width: "500px", zIndex: 2 }}>
        {/* Logo */}
        <Box className={classes.topLogo}>
          <MovieColabLogo />
          <Typography className={classes.brand}>Movie Colab</Typography>
        </Box>

        {/* Card */}
        <Card className={classes.loginCard}>
          <CardContent className={classes.cardContent}>
            <Box className={classes.headerContainer}>
              <Typography className={classes.welcomeTitle}>
                Create An Account
              </Typography>
            </Box>

            {loginError && (
              <Fade in>
                <Alert
                  severity="error"
                  className={classes.errorAlert}
                  onClose={() => setLoginError("")}
                >
                  {loginError}
                </Alert>
              </Fade>
            )}
            {successMessage && (
              <Fade in>
                <Alert
                  severity="success"
                  className={classes.errorAlert}
                  onClose={() => setSuccessMessage("")}
                >
                  {successMessage}
                </Alert>
              </Fade>
            )}

            <Box
              component="form"
              onSubmit={handleSignup}
              className={classes.loginForm}
              noValidate
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "14px",
                }}
              >
                <div style={{ width: "100%" }}>
                  <span className={classes.inputLabel}>First Name</span>
                  <div className={classes.inputWrapper}>
                    <TextField
                      type="text"
                      fullWidth
                      value={formData.first_name}
                      onChange={handleInputChange("first_name")}
                      error={!!errors.first_name}
                      helperText={errors.first_name}
                      InputProps={{
                        disableUnderline: true,
                        style: { fontFamily: "Poppins", marginTop: "5px" },
                      }}
                      className={classes.input}
                      placeholder="Enter First Name"
                    />
                  </div>
                </div>
                <div style={{ width: "100%" }}>
                  <span className={classes.inputLabel}>Last Name</span>
                  <div className={classes.inputWrapper}>
                    <TextField
                      type="text"
                      fullWidth
                      value={formData.last_name}
                      onChange={handleInputChange("last_name")}
                      error={!!errors.last_name}
                      helperText={errors.last_name}
                      InputProps={{
                        disableUnderline: true,
                        style: { fontFamily: "Poppins", marginTop: "5px" },
                      }}
                      className={classes.input}
                      placeholder="Enter Last Name"
                    />
                  </div>
                </div>
              </div>
              {/* Email */}
              <div>
                <span className={classes.inputLabel}>Email</span>
                <div className={classes.inputWrapper}>
                  <TextField
                    type="text"
                    fullWidth
                    value={formData.email}
                    onChange={handleInputChange("email")}
                    error={!!errors.email}
                    helperText={errors.email}
                    InputProps={{
                      disableUnderline: true,
                      style: { fontFamily: "Poppins", marginTop: "5px" },
                    }}
                    className={classes.input}
                    placeholder="Enter email"
                  />
                </div>
              </div>

              {/* Password */}
              <div>
                <span className={classes.inputLabel}>Password</span>
                <div className={classes.inputWrapper}>
                  <TextField
                    type={showPassword ? "text" : "password"}
                    fullWidth
                    value={formData.password}
                    onChange={handleInputChange("password")}
                    error={!!errors.password}
                    helperText={errors.password}
                    InputProps={{
                      disableUnderline: true,
                      style: { fontFamily: "Poppins", marginTop: "5px" },
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={handleTogglePasswordVisibility}
                            tabIndex={-1}
                          >
                            {showPassword ? (
                              <EyeClosed color="white" />
                            ) : (
                              <Eye color="white" />
                            )}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    className={classes.input}
                    placeholder="Your password here"
                  />
                </div>
              </div>

              {/* Button */}
              <Button
                type="submit"
                disabled={isLoading}
                fullWidth
                className={classes.primaryButton}
              >
                {isLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  "Sign Up"
                )}
              </Button>
            </Box>

            {/* Bottom */}
            <Divider className={classes.divider} />

            <Box
              className={classes.linksContainer}
              sx={{
                justifyContent: "center",
              }}
            >
              {/* <Link href="#" className={classes.smallLink}>
                Forgot Password?
              </Link> */}
              <Link href="/login" className={classes.smallLink}>
                Already have an account? Log In
              </Link>
            </Box>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default SignupPage;
